import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Typography, Paper, Tabs, Tab, Button, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip,
  CircularProgress, Alert, useMediaQuery, useTheme, FormControlLabel, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Badge
} from "@mui/material";
import { CheckCircle, HourglassEmpty, CancelPresentation, AddCircle, RemoveCircle, Search, SwapVert, PendingActions, NotificationsActive, History } from "@mui/icons-material";
import api from "../api/axios";
import { useDarkMode } from "../App";
import { useDialog } from "../contexts/DialogProvider";
import { matchesAnySearch } from "../utils/hangul";

const statusConfig = {
  PENDING: { label: "승인 대기", color: "#ffa726", icon: HourglassEmpty },
  APPROVED: { label: "승인됨", color: "#66bb6a", icon: CheckCircle },
  REJECTED: { label: "반려됨", color: "#ef5350", icon: CancelPresentation },
};

const typeConfig = {
  RESERVATION: { label: "레슨 신청", color: "#1976d2", icon: AddCircle },
  CANCELLATION: { label: "레슨 취소", color: "#d32f2f", icon: RemoveCircle },
};

// 상대 시간 표시 (신청 후 경과)
function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "방금";
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  return day === 1 ? "어제" : `${day}일 전`;
}

function LessonRequestManagementPage({ user }) {
  const theme = useTheme();
  const { darkMode } = useDarkMode();
  const { toast } = useDialog();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 승인·반려는 관리자만 가능 — 선생님은 조회 전용 (버튼 숨김, 백엔드에서도 차단)
  const canManage = user?.role === "ADMIN";

  // URL 쿼리(?tab=waitlist 등)로 초기 섹션 지정 (대시보드에서 딥링크로 진입)
  const [searchParams] = useSearchParams();
  const { initialSection, initialHistoryStatus } = (() => {
    const t = searchParams.get("tab");
    if (t === "waitlist" || t === "3") return { initialSection: "waitlist", initialHistoryStatus: "ALL" };
    if (t === "approved" || t === "1") return { initialSection: "history", initialHistoryStatus: "APPROVED" };
    if (t === "rejected" || t === "2") return { initialSection: "history", initialHistoryStatus: "REJECTED" };
    return { initialSection: "pending", initialHistoryStatus: "ALL" };
  })();

  // 섹션: pending(승인 대기) | waitlist(빈자리 대기) | history(처리 내역)
  const [section, setSection] = useState(initialSection);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, request: null });
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [approveDialog, setApproveDialog] = useState({ open: false, request: null, createPractice: true });
  const [scheduleDialog, setScheduleDialog] = useState({ open: false, request: null, lessons: [], teachers: [] });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  // 처리 내역(승인/반려) 검색·상태·유형 필터·정렬
  const [historyQuery, setHistoryQuery] = useState("");
  const [historyStatus, setHistoryStatus] = useState(initialHistoryStatus); // ALL | APPROVED | REJECTED
  const [historyType, setHistoryType] = useState("ALL");
  const [historySort, setHistorySort] = useState("applied"); // applied(신청순) | lesson(레슨날짜순)

  // PC 승인 대기 콘솔: 선택된 신청 + 우측 스케줄 미리보기 데이터
  const [selectedReqId, setSelectedReqId] = useState(null);
  const [panelData, setPanelData] = useState({ lessons: [], teachers: [], overrides: {} });
  const [panelLoading, setPanelLoading] = useState(false);

  // PC 빈자리 대기 콘솔: 선택된 대기 + 우측 주간 표 데이터
  const [wlSelectedId, setWlSelectedId] = useState(null);
  const [wlPanel, setWlPanel] = useState({ lessons: [], teachers: [], weekDates: [], overrides: {} });
  const [wlLoading, setWlLoading] = useState(false);

  // 관리자 블록(고정) 스케줄 — 요일 반복 + 날짜별 예외(is_open)
  const [blockSchedules, setBlockSchedules] = useState([]);

  // 휴일 목록(오늘 이후 전체) — 신청 목록·주간 표·승인 다이얼로그의 휴일 표시용
  const [holidays, setHolidays] = useState([]);
  const isHolidayDate = (dateStr) => holidays.some((h) => h.date === dateStr);
  const isBlockedBySchedule = (teacherId, dateStr, timeStr) => {
    const dow = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][new Date(dateStr).getDay()];
    const bs = blockSchedules.find((b) => b.teacher_id === teacherId && b.day_of_week === dow && b.start_time.slice(0, 5) === timeStr);
    if (!bs) return false;
    const ov = bs.overrides?.find((o) => o.date === dateStr);
    if (ov && ov.is_open) return false;
    return true;
  };

  // 해당 날짜의 유효 근무 시간대 — 변동 스케줄(OFF/ADJUSTED)이 있으면 그것이 우선, 없으면 기본 주간 스케줄
  const effectiveWorkWindows = (teacher, dateStr, overridesMap) => {
    const ov = (overridesMap?.[teacher.id] || []).find((o) => o.date === dateStr);
    if (ov) {
      if (ov.type === "OFF") return [];
      if (ov.type === "ADJUSTED" && ov.work_start && ov.work_end) return [{ start: ov.work_start, end: ov.work_end }];
    }
    const dow = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][new Date(dateStr).getDay()];
    return (teacher.schedules || []).filter((s) => s.day === dow).map((s) => ({ start: s.start, end: s.end }));
  };
  const worksAt = (teacher, dateStr, timeStr, overridesMap) =>
    effectiveWorkWindows(teacher, dateStr, overridesMap).some((w) => timeStr >= String(w.start).slice(0, 5) && timeStr < String(w.end).slice(0, 5));

  useEffect(() => {
    fetchRequests();
    fetchTeachers();
    fetchWaitlist();
    api.get("/block-schedules").then((res) => setBlockSchedules(res.data)).catch(() => setBlockSchedules([]));
    api.get("/holidays").then((res) => setHolidays(res.data)).catch(() => setHolidays([]));
  }, []);

  useEffect(() => {
    if (section === "waitlist") {
      fetchWaitlist();
    } else {
      fetchRequests();
    }
  }, [section]);

  const fetchTeachers = async () => {
    try {
      const res = await api.get("/teachers");
      return res.data;
    } catch (err) {
      console.error("선생님 목록 조회 실패:", err);
      return [];
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await api.get("/lesson-requests");
      setRequests(res.data);
    } catch (err) {
      console.error("신청 목록 조회 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWaitlist = async () => {
    try {
      setWaitlistLoading(true);
      const res = await api.get("/lesson-waitlist/all");
      setWaitlist(res.data);
    } catch (err) {
      console.error("빈자리 대기 목록 조회 실패:", err);
    } finally {
      setWaitlistLoading(false);
    }
  };

  const handleApprove = (request) => {
    // 휴일 신청(RESERVATION)은 처음부터 경고 + 확인 플래그 흐름으로 연다 (서버 409 왕복 없이)
    setApproveDialog({
      open: true, request, createPractice: true,
      holidayWarn: request.type === "RESERVATION" && isHolidayDate(request.date),
    });
  };

  const handleConfirmApprove = async (confirmHoliday) => {
    const { request, createPractice } = approveDialog;
    try {
      setActionLoading(true);
      await api.post(`/lesson-requests/${request.id}/approve`, {
        skip_practice: !createPractice,
        ...(confirmHoliday === true ? { confirm_holiday: true } : {}),
      });
      toast("승인되었습니다.");
      setApproveDialog({ open: false, request: null, createPractice: true });
      fetchRequests();
    } catch (err) {
      // 신청 이후 휴일로 지정된 날짜: 서버가 확인 플래그를 요구 → 같은 다이얼로그에서 재확인
      if (err?.response?.status === 409 && err?.response?.data?.code === "HOLIDAY_CONFIRM_REQUIRED") {
        setApproveDialog((d) => ({ ...d, holidayWarn: true }));
      } else {
        toast(err?.response?.data?.message || "승인 실패", "error");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (request) => {
    setRejectDialog({ open: true, request });
    setRejectionReason("");
  };

  const handleConfirmReject = async () => {
    try {
      setActionLoading(true);
      await api.post(`/lesson-requests/${rejectDialog.request.id}/reject`, {
        rejection_reason: rejectionReason,
      });
      toast("반려되었습니다.");
      setRejectDialog({ open: false, request: null });
      fetchRequests();
    } catch (err) {
      toast(err?.response?.data?.message || "반려 실패", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // 요청 날짜의 스케줄 컨텍스트(해당 날짜 레슨 + 선생님 목록) 조회
  const fetchScheduleContext = async (request) => {
    const teachers = await fetchTeachers();
    const requestDate = new Date(request.date);
    const dayOfWeek = requestDate.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(requestDate);
    monday.setDate(requestDate.getDate() + mondayOffset);
    const weekStart = monday.toISOString().split('T')[0];
    const res = await api.get("/lessons", { params: { week_start: weekStart } });
    return {
      teachers,
      lessons: res.data.filter((lesson) => lesson.date === request.date),
    };
  };

  const handleViewSchedule = async (request) => {
    try {
      setScheduleLoading(true);
      setScheduleDialog({ open: true, request, lessons: [], teachers: [] });
      const { teachers, lessons } = await fetchScheduleContext(request);
      setScheduleDialog((prev) => ({ ...prev, lessons, teachers }));
    } catch (err) {
      console.error("스케줄 조회 실패:", err);
      toast("스케줄 조회에 실패했습니다.", "error");
      setScheduleDialog({ open: false, request: null, lessons: [], teachers: [] });
    } finally {
      setScheduleLoading(false);
    }
  };

  // 특정 날짜가 속한 주(월~토)의 전체 레슨 + 선생님 + 요일 날짜 + 변동 스케줄 조회
  const fetchWeekContext = async (dateStr) => {
    const teachers = await fetchTeachers();
    const base = new Date(dateStr);
    const dow = base.getDay();
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(base);
    monday.setDate(base.getDate() + mondayOffset);
    const weekStart = monday.toISOString().split("T")[0];
    const weekDates = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d.toISOString().split("T")[0];
    });
    const [res, overrideLists] = await Promise.all([
      api.get("/lessons", { params: { week_start: weekStart } }),
      Promise.all(teachers.map((t) => api.get(`/teachers/${t.id}/schedule-overrides`).then((r) => r.data).catch(() => []))),
    ]);
    const overrides = {};
    teachers.forEach((t, i) => { overrides[t.id] = overrideLists[i]; });
    return { teachers, lessons: res.data, weekDates, overrides };
  };

  // PC 승인 대기 콘솔: 신청 선택 시 우측 패널용 주간 레슨 로드
  const selectRequest = async (request) => {
    setSelectedReqId(request.id);
    setPanelData({ lessons: [], teachers: [], weekDates: [], overrides: {} });
    setPanelLoading(true);
    try {
      setPanelData(await fetchWeekContext(request.date));
    } catch (err) {
      console.error("스케줄 조회 실패:", err);
    } finally {
      setPanelLoading(false);
    }
  };

  // PC 빈자리 대기 콘솔: 대기 항목 선택 시 우측 패널용 주간 레슨 로드
  const selectWaitlist = async (entry) => {
    setWlSelectedId(entry.id);
    setWlPanel({ lessons: [], teachers: [], weekDates: [], overrides: {} });
    setWlLoading(true);
    try {
      setWlPanel(await fetchWeekContext(entry.date));
    } catch (err) {
      console.error("스케줄 조회 실패:", err);
    } finally {
      setWlLoading(false);
    }
  };

  // 승인 대기 목록 갱신 시 가장 오래된 신청을 자동 선택 (PC)
  useEffect(() => {
    if (isMobile || section !== "pending") return;
    const pending = requests
      .filter((r) => r.status === "PENDING")
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    if (pending.length === 0) {
      if (selectedReqId !== null) setSelectedReqId(null);
      return;
    }
    if (!pending.some((r) => r.id === selectedReqId)) {
      selectRequest(pending[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, section, isMobile]);

  // 빈자리 대기 D-day 계산 및 임박순 정렬 (다가오는 항목 먼저, 지난 항목은 뒤로)
  const ddayOf = (dateStr) => {
    const s = new Date();
    s.setHours(0, 0, 0, 0);
    return Math.round((new Date(`${dateStr}T00:00:00`) - s) / 86400000);
  };
  const sortWaitlist = (list) =>
    [...list].sort((a, b) => {
      const ap = ddayOf(a.date) < 0;
      const bp = ddayOf(b.date) < 0;
      if (ap !== bp) return ap ? 1 : -1;
      return `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`);
    });

  // 빈자리 대기 목록 갱신 시 가장 임박한 항목 자동 선택 (PC)
  useEffect(() => {
    if (isMobile || section !== "waitlist") return;
    if (waitlist.length === 0) {
      if (wlSelectedId !== null) setWlSelectedId(null);
      return;
    }
    const sorted = sortWaitlist(waitlist);
    if (!sorted.some((w) => w.id === wlSelectedId)) {
      selectWaitlist(sorted[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitlist, section, isMobile]);

  // 승인 대기 목록 (오래된 순)
  const pendingRequests = requests
    .filter((r) => r.status === "PENDING")
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  // 빈자리 대기(대기 알림) 카드 렌더링
  const renderWaitlistCard = (entry) => {
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    const dateObj = new Date(entry.date);
    const dayOfWeek = dayNames[dateObj.getDay()];
    const dateText = `${entry.date}(${dayOfWeek})`;
    const timeText = entry.start_time ? entry.start_time.slice(0, 5) : "-";
    const appliedAt = entry.createdAt || entry.created_at;

    return (
      <Paper
        key={entry.id}
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          borderLeft: "4px solid #9c27b0",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
          <Chip
            icon={<HourglassEmpty sx={{ fontSize: 14 }} />}
            label="빈자리 대기"
            size="small"
            sx={{
              backgroundColor: "#9c27b022",
              color: "#9c27b0",
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          />
        </Box>

        <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
          <strong>{entry.user?.name}</strong>님 →{" "}
          <strong>{entry.teacher?.name ? `${entry.teacher.name} 선생님` : "선생님 지정 없음"}</strong>
        </Typography>
        <Typography variant="body2" sx={{ mb: appliedAt ? 1.5 : 0, color: "text.primary" }}>
          📅 {dateText} &nbsp; 🕐 {timeText}
        </Typography>

        {appliedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            신청: {new Date(appliedAt).toLocaleString("ko-KR", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        )}
      </Paper>
    );
  };

  // 모바일용 카드 렌더링
  const renderMobileCard = (request) => {
    const config = statusConfig[request.status];
    const typeConf = typeConfig[request.type];
    const Icon = config.icon;
    const TypeIcon = typeConf?.icon;

    const lessonDate = new Date(request.date);
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    const dayOfWeek = dayNames[lessonDate.getDay()];
    const timeText = `${request.start_time.slice(0, 5)} ~ ${request.end_time.slice(0, 5)}`;
    const dateText = `${request.date}(${dayOfWeek})`;

    return (
      <Paper
        key={request.id}
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          borderLeft: `4px solid ${typeConf?.color || "#ccc"}`,
        }}
      >
        {/* 상단: 타입 + 상태 */}
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
          <Chip
            icon={TypeIcon ? <TypeIcon sx={{ fontSize: 14 }} /> : undefined}
            label={typeConf?.label}
            size="small"
            sx={{
              backgroundColor: `${typeConf?.color}22`,
              color: typeConf?.color,
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          />
          {request.status !== "PENDING" && (
            <Chip
              label={config.label}
              icon={<Icon sx={{ fontSize: 14 }} />}
              size="small"
              sx={{
                backgroundColor: `${config.color}22`,
                color: config.color,
                fontSize: "0.75rem",
              }}
            />
          )}
        </Box>

        {/* 메인 정보 */}
        <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
          <strong>{request.user?.name}</strong>님 → <strong>{request.teacher?.name}</strong> 선생님
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5, color: "text.primary" }}>
          📅 {dateText} &nbsp; 🕐 {timeText}
          {isHolidayDate(request.date) && (
            <Box component="span" sx={{ color: "#f44336", fontWeight: 700 }}> · 휴일</Box>
          )}
        </Typography>

        {/* 신청 시간 */}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          신청: {new Date(request.createdAt).toLocaleString("ko-KR", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Typography>

        {/* 반려 사유 */}
        {request.rejection_reason && (
          <Alert severity="error" sx={{ py: 0.5, mb: 1.5 }}>
            <Typography variant="caption">
              <strong>반려 사유:</strong> {request.rejection_reason}
            </Typography>
          </Alert>
        )}

        {/* 액션 버튼 */}
        {request.status === "PENDING" && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleViewSchedule(request)}
              disabled={actionLoading}
              sx={{ flex: 1 }}
            >
              스케줄
            </Button>
            {canManage && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => handleApprove(request)}
                  disabled={actionLoading}
                  sx={{ flex: 1 }}
                >
                  승인
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleRejectClick(request)}
                  disabled={actionLoading}
                  sx={{ flex: 1 }}
                >
                  반려
                </Button>
              </>
            )}
          </Stack>
        )}
      </Paper>
    );
  };

  // PC 승인 대기 콘솔에서 선택된 신청
  const selectedRequest = pendingRequests.find((r) => r.id === selectedReqId) || null;
  const dayNamesKo = ["일", "월", "화", "수", "목", "금", "토"];

  // 탭 라벨 + 카운트 배지 (처리 대기 항목은 badgeColor로 강조)
  const tabLabel = (text, count, badgeColor) => (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, whiteSpace: "nowrap" }}>
      {text}
      <Box
        component="span"
        sx={{
          minWidth: 18, height: 18, px: 0.5, borderRadius: "9px",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontSize: "0.68rem", fontWeight: 700, lineHeight: 1,
          bgcolor: badgeColor && count > 0 ? badgeColor : (darkMode ? "grey.700" : "grey.300"),
          color: badgeColor && count > 0 ? "#fff" : "text.secondary",
        }}
      >
        {count}
      </Box>
    </Box>
  );

  // 승인 대기 좌측 목록의 신청 행
  const renderPendingRow = (request) => {
    const typeConf = typeConfig[request.type];
    const dow = dayNamesKo[new Date(request.date).getDay()];
    const selected = request.id === selectedReqId;
    return (
      <Box
        key={request.id}
        onClick={() => selectRequest(request)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1.2,
          borderRadius: 1.5,
          cursor: "pointer",
          border: selected ? "1px solid" : "0.5px solid",
          borderColor: selected ? "primary.main" : "divider",
          bgcolor: selected ? (darkMode ? "rgba(25,118,210,0.15)" : "#e3f2fd") : (darkMode ? "grey.900" : "#fff"),
          transition: "0.15s",
          "&:hover": { borderColor: "primary.main" },
        }}
      >
        <Chip
          label={request.type === "RESERVATION" ? "예약" : "취소"}
          size="small"
          sx={{ flex: "none", height: 22, fontSize: "0.7rem", fontWeight: 600, backgroundColor: `${typeConf?.color}22`, color: typeConf?.color, "& .MuiChip-label": { px: 1 } }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {request.user?.name} <span style={{ color: "#9e9e9e" }}>→</span> {request.teacher?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(request.date).getMonth() + 1}/{new Date(request.date).getDate()}({dow}) {request.start_time.slice(0, 5)}
            {isHolidayDate(request.date) && (
              <Box component="span" sx={{ color: "#f44336", fontWeight: 700 }}> · 휴일</Box>
            )}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ flex: "none" }}>
          {timeAgo(request.createdAt)}
        </Typography>
      </Box>
    );
  };

  // 레슨 페이지와 동일 표 양식: 행=시간, 셀 안에 선생님 박스(초록=신규·빨강=취소·주황=예약됨·파랑=근무가능)
  const renderScheduleGrid = (request, lessons, teachers, span = 2) => {
    const getTimeSlots = () => {
      const [reqHour, reqMin] = request.start_time.slice(0, 5).split(":").map(Number);
      const timeSlots = [];
      for (let offset = -span; offset <= span; offset++) {
        let hour = reqHour;
        let min = reqMin + offset * 30;
        while (min < 0) { min += 60; hour -= 1; }
        while (min >= 60) { min -= 60; hour += 1; }
        if (hour >= 12 && hour < 22) {
          const timeStr = `${String(hour).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
          const endMin = min + 30;
          const endHour = endMin >= 60 ? hour + 1 : hour;
          const finalEndMin = endMin >= 60 ? endMin - 60 : endMin;
          const endTimeStr = `${String(endHour).padStart(2, "0")}:${String(finalEndMin).padStart(2, "0")}`;
          timeSlots.push({ start: timeStr, end: endTimeStr });
        }
      }
      return timeSlots;
    };
    const timeSlots = getTimeSlots();
    const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const dayOfWeek = dayNames[new Date(request.date).getDay()];
    const getAvailableTeachers = (timeStr) =>
      teachers.filter((teacher) => {
        if (!teacher.schedules || teacher.schedules.length === 0) return false;
        return teacher.schedules.some(
          (s) => s.day === dayOfWeek && timeStr >= s.start.slice(0, 5) && timeStr < s.end.slice(0, 5)
        );
      });
    const getLessonForSlot = (teacherId, timeStr) =>
      lessons.find((l) => l.teacher_id === teacherId && l.start_time.slice(0, 5) === timeStr);
    const requestTime = request.start_time.slice(0, 5);
    const requestTeacherId = request.teacher_id;
    const lineColor = darkMode ? "rgba(255,255,255,0.15)" : "#ddd";

    return (
      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "clamp(52px, 6vw, 72px) 1fr" }}>
          {timeSlots.map((slot, idx) => {
            const availableTeachers = getAvailableTeachers(slot.start);
            const isTargetTime = slot.start === requestTime;
            const rowTop = idx === 0 ? "none" : `1px solid ${lineColor}`;
            return (
              <React.Fragment key={idx}>
                <Box sx={{
                  p: 0.75,
                  bgcolor: isTargetTime ? (darkMode ? "rgba(255, 249, 196, 0.18)" : "#fff9c4") : (darkMode ? "grey.900" : "#f7f7f7"),
                  borderRight: `1px solid ${lineColor}`,
                  borderTop: rowTop,
                  fontSize: "0.72rem",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  lineHeight: 1.3, fontWeight: isTargetTime ? "bold" : "normal",
                }}>
                  <div>{slot.start}</div>
                  <div style={{ fontSize: "0.62rem", color: darkMode ? "#aaa" : "#888" }}>~</div>
                  <div>{slot.end}</div>
                </Box>

                <Box sx={{
                  p: 0.75,
                  borderTop: rowTop,
                  bgcolor: isTargetTime ? (darkMode ? "rgba(255, 249, 196, 0.1)" : "#fffde7") : "transparent",
                  display: "flex", flexWrap: "wrap", gap: 0.5, alignContent: "center",
                }}>
                  {availableTeachers.length === 0 ? (
                    <Box sx={{
                      flex: 1, minHeight: 34, display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: darkMode ? "grey.800" : "#f5f5f5", color: darkMode ? "grey.500" : "#999",
                      fontSize: "0.72rem", borderRadius: 1,
                    }}>
                      선생님 미출근
                    </Box>
                  ) : (
                    availableTeachers.map((teacher) => {
                      const lesson = getLessonForSlot(teacher.id, slot.start);
                      const isNewReservation = request.type === "RESERVATION" && isTargetTime && teacher.id === requestTeacherId;
                      const isCancelTarget = request.type === "CANCELLATION" && lesson && lesson.id === request.lesson_id;
                      let displayContent;
                      let bgcolor;
                      let extraStyle = {};
                      if (isNewReservation) {
                        displayContent = (<><b>{request.user?.name}</b><br />({teacher.name} 선생님)<div style={{ fontSize: "0.62rem", marginTop: 2 }}>신규 예약</div></>);
                        bgcolor = "#4caf50"; extraStyle = { color: "#fff" };
                      } else if (isCancelTarget) {
                        displayContent = (<><b>{lesson.user?.name}</b><br />({teacher.name} 선생님)<div style={{ fontSize: "0.62rem", marginTop: 2 }}>취소 예정</div></>);
                        bgcolor = "#ef5350"; extraStyle = { color: "#fff", textDecoration: "line-through" };
                      } else if (lesson) {
                        displayContent = (<>{lesson.user?.name}<br />({teacher.name} 선생님)</>);
                        bgcolor = darkMode ? "rgba(255, 224, 178, 0.3)" : "#ffe0b2";
                      } else {
                        displayContent = (<>{teacher.name}<br />선생님</>);
                        bgcolor = darkMode ? "rgba(144, 202, 249, 0.2)" : "#e3f2fd";
                      }
                      return (
                        <Box key={teacher.id} sx={{
                          flex: "1 1 96px", minHeight: 40, textAlign: "center", fontSize: "0.72rem",
                          bgcolor, color: darkMode ? "#eee" : "#222", fontWeight: 500, borderRadius: 1,
                          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                          wordBreak: "break-word", px: 0.5, py: 0.5, lineHeight: 1.25, ...extraStyle,
                        }}>
                          {displayContent}
                        </Box>
                      );
                    })
                  )}
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      </Paper>
    );
  };

  // 레슨 페이지와 동일한 주간 표(요일 열 × 시간 행). mark(대상 슬롯)만 강조: kind = new(신규)/cancel(취소)/wait(대기)
  const renderWeekGrid = (mark, lessons, teachers, weekDates, span = 4, overrides = {}) => {
    if (!weekDates || weekDates.length === 0) return null;
    const [reqHour, reqMin] = mark.time.split(":").map(Number);
    const timeSlots = [];
    for (let offset = -span; offset <= span; offset++) {
      let hour = reqHour;
      let min = reqMin + offset * 30;
      while (min < 0) { min += 60; hour -= 1; }
      while (min >= 60) { min -= 60; hour += 1; }
      if (hour >= 12 && hour < 22) {
        timeSlots.push(`${String(hour).padStart(2, "0")}:${String(min).padStart(2, "0")}`);
      }
    }
    const dayKo = ["일", "월", "화", "수", "목", "금", "토"];
    const reqTime = mark.time;
    const line = darkMode ? "rgba(255,255,255,0.08)" : "#edf0f2";
    const headBg = darkMode ? "grey.900" : "#fafbfc";
    const reqTint = darkMode ? "rgba(25,118,210,0.06)" : "#f5f9ff";
    const availAt = (dateStr, timeStr) => teachers.filter((t) => worksAt(t, dateStr, timeStr, overrides));
    const lessonAt = (dateStr, teacherId, timeStr) =>
      lessons.find((l) => l.date === dateStr && l.teacher_id === teacherId && l.start_time.slice(0, 5) === timeStr);

    const chipBase = {
      height: 24, borderRadius: 1, px: 0.75, display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: "0.7rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%",
    };

    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{
            display: "grid",
            gridTemplateColumns: `56px repeat(${weekDates.length}, minmax(94px, 1fr))`,
            gridAutoRows: "minmax(44px, auto)",
            minWidth: 620,
          }}>
            <Box sx={{ bgcolor: headBg, borderBottom: `1px solid ${line}` }} />
            {weekDates.map((d) => {
              const isReqDay = d === mark.date;
              const holiday = isHolidayDate(d);
              const dnum = new Date(d).getDay();
              return (
                <Box key={d} sx={{
                  py: 0.75, textAlign: "center", lineHeight: 1.25,
                  borderLeft: `1px solid ${line}`,
                  borderBottom: isReqDay ? "2px solid #1976d2" : `1px solid ${line}`,
                  bgcolor: holiday ? (darkMode ? "#1a1a1a" : "#222") : isReqDay ? (darkMode ? "rgba(25,118,210,0.14)" : "#eaf3ff") : headBg,
                }}>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: holiday ? "#f44336" : isReqDay ? "primary.main" : (dnum === 0 ? "error.main" : "text.primary") }}>
                    {dayKo[dnum]}{holiday ? " · 휴일" : ""}
                  </Typography>
                  <Typography sx={{ fontSize: "0.62rem", color: holiday ? "#f44336" : "text.secondary" }}>{d.slice(5)}</Typography>
                </Box>
              );
            })}

            {timeSlots.map((time, idx) => (
              <React.Fragment key={time}>
                <Box sx={{
                  bgcolor: headBg, borderTop: idx === 0 ? "none" : `1px solid ${line}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.72rem", fontWeight: time === reqTime ? 700 : 500,
                  color: time === reqTime ? "primary.main" : "text.secondary",
                }}>
                  {time}
                </Box>
                {weekDates.map((d) => {
                  // 휴일: 셀 내용을 통째로 차단 표시 (시간표·보강 다이얼로그와 동일)
                  if (isHolidayDate(d)) {
                    return (
                      <Box key={d} sx={{
                        borderLeft: `1px solid ${line}`, borderTop: idx === 0 ? "none" : `1px solid ${line}`,
                        bgcolor: darkMode ? "#1a1a1a" : "#222",
                        p: 0.5, display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#f44336" }}>휴일</Typography>
                      </Box>
                    );
                  }
                  const isReqDay = d === mark.date;
                  const avail = availAt(d, time);
                  // 대기(wait) 대상 슬롯: 지정 선생님이 미출근 시간대여도 보라색 대기 칸이 보이도록 강제 포함
                  const isTargetCell = isReqDay && time === reqTime && mark.kind === "wait";
                  let cellTeachers = avail;
                  let offDutyTeacherId = null;
                  let waitPlaceholder = false;
                  if (isTargetCell) {
                    if (mark.teacherId != null && !avail.some((t) => t.id === mark.teacherId)) {
                      const target = teachers.find((t) => t.id === mark.teacherId);
                      if (target) {
                        cellTeachers = [...avail, target];
                        offDutyTeacherId = target.id;
                      }
                    } else if (mark.teacherId == null && avail.length === 0) {
                      waitPlaceholder = true;
                    }
                  }
                  return (
                    <Box key={d} sx={{
                      borderLeft: `1px solid ${line}`, borderTop: idx === 0 ? "none" : `1px solid ${line}`,
                      bgcolor: isReqDay ? reqTint : "transparent",
                      p: 0.5, display: "flex", flexDirection: "column", gap: 0.5, justifyContent: "center",
                    }}>
                      {waitPlaceholder && (
                        <Box
                          title={`미출근 시간대 — 출근 선생님 없음 (${mark.memberName} 대기)`}
                          sx={{ ...chipBase, bgcolor: "#8e24aa", color: "#fff", fontWeight: 600, animation: "blink 1.15s ease-in-out infinite" }}
                        >
                          대기 · 미출근
                        </Box>
                      )}
                      {cellTeachers.map((t) => {
                        const lesson = lessonAt(d, t.id, time);
                        const isBlockLesson = !!lesson && (lesson.user?.role === "ADMIN" || lesson.user?.role === "TEACHER");
                        const blocked = isBlockLesson || (!lesson && isBlockedBySchedule(t.id, d, time));
                        const isTargetSlot = isReqDay && time === reqTime && (mark.teacherId == null || t.id === mark.teacherId);
                        let label;
                        let title;
                        let chipSx;
                        if (isTargetSlot && mark.kind === "new" && !lesson && !blocked) {
                          label = `${mark.memberName} · 신규`;
                          title = `${t.name} 선생님 · ${mark.memberName} 신규 예약`;
                          chipSx = { ...chipBase, bgcolor: "#43a047", color: "#fff", fontWeight: 600 };
                        } else if (isTargetSlot && mark.kind === "cancel" && lesson && !isBlockLesson) {
                          label = `${lesson.user?.name} · 취소`;
                          title = `${t.name} 선생님 · ${lesson.user?.name} 취소 예정`;
                          chipSx = { ...chipBase, bgcolor: "#e53935", color: "#fff", fontWeight: 600, textDecoration: "line-through" };
                        } else if (isTargetSlot && mark.kind === "wait") {
                          const blink = "blink 1.15s ease-in-out infinite";
                          if (blocked) {
                            label = "블록 · 대기";
                            title = `${t.name} 선생님 · 관리자 블록 시간 (${mark.memberName} 대기)`;
                            chipSx = { ...chipBase, bgcolor: "#8e24aa", color: "#fff", fontWeight: 600, animation: blink };
                          } else if (lesson) {
                            // AdminLessonPage처럼: 기존 예약자 / 선생님 이름 2줄
                            label = (
                              <>
                                <Box component="span" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{lesson.user?.name}</Box>
                                <Box component="span" sx={{ fontSize: "0.6rem", lineHeight: 1.1, opacity: 0.9 }}>{t.name} 선생님</Box>
                              </>
                            );
                            title = `${t.name} 선생님 · 현재 ${lesson.user?.name}님 예약 중 — 취소되면 ${mark.memberName}님에게 빈자리 알림 발송`;
                            chipSx = { ...chipBase, flexDirection: "column", height: "auto", minHeight: 24, py: 0.25, whiteSpace: "normal", bgcolor: "#8e24aa", color: "#fff", fontWeight: 600, animation: blink };
                          } else if (t.id === offDutyTeacherId) {
                            label = "대기 · 미출근";
                            title = `${t.name} 선생님 · 미출근 시간대 (${mark.memberName} 대기)`;
                            chipSx = { ...chipBase, bgcolor: "#8e24aa", color: "#fff", fontWeight: 600, animation: blink };
                          } else {
                            label = "대기 · 빈자리";
                            title = `${t.name} 선생님 · 빈자리 (${mark.memberName} 대기)`;
                            chipSx = { ...chipBase, bgcolor: "transparent", border: "1.5px solid #8e24aa", color: "#8e24aa", fontWeight: 600, animation: blink };
                          }
                        } else if (blocked) {
                          label = "블록";
                          title = `${t.name} 선생님 · 관리자 블록(예약 차단)`;
                          chipSx = { ...chipBase, bgcolor: darkMode ? "rgba(255,255,255,0.13)" : "#e8eaed", color: darkMode ? "#bdbdbd" : "#5f6368", fontWeight: 500 };
                        } else if (lesson) {
                          label = lesson.user?.name;
                          title = `${t.name} 선생님 · ${lesson.user?.name}`;
                          chipSx = { ...chipBase, bgcolor: darkMode ? "rgba(255,167,38,0.16)" : "#fff3e0", color: darkMode ? "#ffb74d" : "#e65100", fontWeight: 500 };
                        } else {
                          label = t.name;
                          title = `${t.name} 선생님 · 예약 없음`;
                          chipSx = { ...chipBase, bgcolor: "transparent", border: `1px solid ${line}`, color: "text.secondary", fontWeight: 400 };
                        }
                        return (
                          <Box key={t.id} title={title} sx={chipSx}>{label}</Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Paper>
    );
  };

  // 승인 대기 우측 검토 패널 (선택 신청 주간 표 + 승인/반려)
  const renderReviewPanel = (request) => {
    const typeConf = typeConfig[request.type];
    const dow = dayNamesKo[new Date(request.date).getDay()];
    const reqTime = request.start_time.slice(0, 5);
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", pb: 1, mb: 1.25, borderBottom: 1, borderColor: "divider" }}>
          <Chip
            label={typeConf?.label}
            size="small"
            sx={{ backgroundColor: `${typeConf?.color}22`, color: typeConf?.color, fontWeight: 600 }}
          />
          <Typography variant="subtitle1" fontWeight="bold">
            {request.user?.name} <span style={{ color: "#9e9e9e" }}>→</span> {request.teacher?.name} 선생님
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {request.date} ({dow}) {reqTime} ~ {request.end_time.slice(0, 5)} · 신청 {timeAgo(request.createdAt)}
          </Typography>
          {isHolidayDate(request.date) && (
            <Chip label="휴일" size="small" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#f4433622", color: "#f44336" }} />
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>
            {canManage ? (
              <>
                <Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => handleApprove(request)} disabled={actionLoading}>
                  승인
                </Button>
                <Button variant="outlined" color="error" startIcon={<CancelPresentation />} onClick={() => handleRejectClick(request)} disabled={actionLoading}>
                  반려
                </Button>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">조회 전용 · 승인/반려는 관리자만 가능합니다</Typography>
            )}
          </Stack>
        </Box>

        {request.type === "RESERVATION" ? (
          <Alert severity="success" sx={{ mb: 1.25, py: 0.25, "& .MuiAlert-message": { py: 0.5 } }}>
            <Typography variant="caption">
              <strong>승인 시:</strong> 초록색으로 표시된 레슨이 추가됩니다.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25, "& .MuiAlert-message": { py: 0.5 } }}>
            <Typography variant="caption">
              <strong>승인 시:</strong> 빨간색으로 표시된 레슨이 삭제되고, {request.user?.name}님의 티켓이 1개 복구됩니다.
            </Typography>
          </Alert>
        )}

        {panelLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {renderWeekGrid(
              { date: request.date, time: request.start_time.slice(0, 5), teacherId: request.teacher_id, kind: request.type === "RESERVATION" ? "new" : "cancel", memberName: request.user?.name },
              panelData.lessons, panelData.teachers, panelData.weekDates || [], 2, panelData.overrides || {}
            )}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.75 }}>
              {[
                { t: "예약 없음", ghost: true },
                { t: "예약됨", bg: darkMode ? "rgba(255,167,38,0.16)" : "#fff3e0" },
                { t: "블록", bg: darkMode ? "rgba(255,255,255,0.13)" : "#e8eaed" },
                { t: "신규 예약", bg: "#43a047" },
                { t: "취소 예정", bg: "#e53935" },
                { t: "휴일", bg: darkMode ? "#1a1a1a" : "#222" },
              ].map((lg) => (
                <Box key={lg.t} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: lg.ghost ? "transparent" : lg.bg, border: lg.ghost ? "1px solid" : "none", borderColor: "divider" }} />
                  <Typography variant="caption" color="text.secondary">{lg.t}</Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  };

  // PC 승인 대기: 좌우 2단 콘솔
  const renderPendingConsole = () => {
    if (pendingRequests.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="text.secondary">승인 대기 신청이 없습니다.</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "260px minmax(0, 1fr)", gap: 2, height: "100%", minHeight: 0 }}>
        <Box sx={{ overflowY: "auto", minHeight: 0, pr: 0.5 }}>
          <Stack spacing={1}>
            {pendingRequests.map(renderPendingRow)}
          </Stack>
        </Box>
        <Box sx={{ overflowY: "auto", minHeight: 0 }}>
          {selectedRequest ? (
            renderReviewPanel(selectedRequest)
          ) : (
            <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, py: 8, textAlign: "center" }}>
              <Typography color="text.secondary">왼쪽에서 신청을 선택하세요.</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // 처리 내역(승인 + 반려) — PC 표 / 모바일 카드 겸용
  const renderHistory = () => {
    const q = historyQuery.trim();
    const rows = requests
      .filter((r) => r.status === "APPROVED" || r.status === "REJECTED")
      .filter((r) => {
        if (historyStatus !== "ALL" && r.status !== historyStatus) return false;
        if (historyType !== "ALL" && r.type !== historyType) return false;
        // 초성 검색 지원 — 'ㅎㄱㄷ' 로 '홍길동' 이 잡힌다.
        if (!matchesAnySearch([r.user?.name, r.user?.username], q)) return false;
        return true;
      })
      .sort((a, b) => {
        if (historySort === "lesson") {
          return `${b.date}${b.start_time}`.localeCompare(`${a.date}${a.start_time}`);
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
    const fmtDateTime = (d) => {
      const dt = new Date(d);
      const mi = String(dt.getMinutes()).padStart(2, "0");
      return `${dt.getMonth() + 1}/${dt.getDate()} ${String(dt.getHours()).padStart(2, "0")}:${mi}`;
    };

    const statusFilters = [
      { v: "ALL", label: "전체" },
      { v: "APPROVED", label: "승인" },
      { v: "REJECTED", label: "반려" },
    ];
    const typeFilters = [
      { v: "ALL", label: "전체 유형" },
      { v: "RESERVATION", label: "예약" },
      { v: "CANCELLATION", label: "취소" },
    ];
    const showStatusCol = historyStatus === "ALL";
    const showReasonCol = historyStatus !== "APPROVED";

    return (
      <Box>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <TextField
            size="small"
            placeholder="이름 또는 초성으로 검색"
            value={historyQuery}
            onChange={(e) => setHistoryQuery(e.target.value)}
            InputProps={{ startAdornment: <Search sx={{ fontSize: 18, color: "text.disabled", mr: 0.75 }} /> }}
            sx={{ flex: "0 1 220px", minWidth: 150 }}
          />
          <Stack direction="row" spacing={0.75}>
            {statusFilters.map((f) => (
              <Chip
                key={f.v}
                label={f.label}
                size="small"
                color={historyStatus === f.v ? "primary" : "default"}
                variant={historyStatus === f.v ? "filled" : "outlined"}
                onClick={() => setHistoryStatus(f.v)}
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Stack>
          <Box sx={{ width: "1px", height: 20, bgcolor: "divider", display: { xs: "none", sm: "block" } }} />
          <Stack direction="row" spacing={0.75}>
            {typeFilters.map((f) => (
              <Chip
                key={f.v}
                label={f.label}
                size="small"
                color={historyType === f.v ? "secondary" : "default"}
                variant={historyType === f.v ? "filled" : "outlined"}
                onClick={() => setHistoryType(f.v)}
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Stack>
          <Button size="small" startIcon={<SwapVert />} onClick={() => setHistorySort((s) => (s === "applied" ? "lesson" : "applied"))}>
            {historySort === "applied" ? "신청순" : "레슨날짜순"}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>{rows.length}건</Typography>
        </Stack>

        {rows.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <Typography color="text.secondary">처리된 내역이 없습니다.</Typography>
          </Box>
        ) : isMobile ? (
          <Box>{rows.map(renderMobileCard)}</Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ "& thead th": { backgroundColor: darkMode ? "grey.900" : "#fafafa" } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>신청 일자</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>처리 일자</TableCell>
                  {showStatusCol && <TableCell sx={{ fontWeight: 600 }}>상태</TableCell>}
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>레슨 일시</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>유형</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>회원</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>선생님</TableCell>
                  {showReasonCol && <TableCell sx={{ fontWeight: 600 }}>반려 사유</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const typeConf = typeConfig[r.type];
                  const stConf = statusConfig[r.status];
                  const dow = dayNamesKo[new Date(r.date).getDay()];
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDateTime(r.createdAt)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>{r.updatedAt ? fmtDateTime(r.updatedAt) : "—"}</TableCell>
                      {showStatusCol && (
                        <TableCell>
                          <Chip label={stConf?.label} size="small" sx={{ backgroundColor: `${stConf?.color}22`, color: stConf?.color, fontWeight: 600, height: 22, fontSize: "0.72rem" }} />
                        </TableCell>
                      )}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{r.date}({dow}) {r.start_time.slice(0, 5)}~{r.end_time.slice(0, 5)}</TableCell>
                      <TableCell>
                        <Chip label={typeConf?.label} size="small" sx={{ backgroundColor: `${typeConf?.color}22`, color: typeConf?.color, fontWeight: 600, height: 22, fontSize: "0.72rem" }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{r.user?.name}</TableCell>
                      <TableCell>{r.teacher?.name}</TableCell>
                      {showReasonCol && (
                        <TableCell sx={{ maxWidth: 240 }}>
                          <Typography variant="body2" color="text.secondary" noWrap title={r.rejection_reason || ""}>
                            {r.status === "REJECTED" ? (r.rejection_reason || "—") : "—"}
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  // 빈자리 대기 좌측 목록의 한 행
  const renderWaitlistRow = (e, count, isSelected) => {
    const diff = ddayOf(e.date);
    const dow = dayNamesKo[new Date(e.date).getDay()];
    let ddLabel;
    let ddColor;
    let ddBg;
    if (diff < 0) { ddLabel = "지남"; ddColor = "text.disabled"; ddBg = darkMode ? "rgba(255,255,255,0.06)" : "#f0f0f0"; }
    else if (diff === 0) { ddLabel = "오늘"; ddColor = "#c62828"; ddBg = darkMode ? "rgba(244,67,54,0.2)" : "#ffebee"; }
    else if (diff === 1) { ddLabel = "내일"; ddColor = "#e65100"; ddBg = darkMode ? "rgba(255,152,0,0.2)" : "#fff3e0"; }
    else { ddLabel = `D-${diff}`; ddColor = "#1565c0"; ddBg = darkMode ? "rgba(144,202,249,0.18)" : "#e3f2fd"; }
    return (
      <Box
        key={e.id}
        onClick={() => selectWaitlist(e)}
        sx={{
          display: "flex", alignItems: "center", gap: 1, p: 1.1, cursor: "pointer",
          border: isSelected ? "1px solid" : "0.5px solid",
          borderColor: isSelected ? "primary.main" : "divider",
          bgcolor: isSelected ? (darkMode ? "rgba(25,118,210,0.15)" : "#e3f2fd") : (darkMode ? "grey.900" : "#fff"),
          borderRadius: 1.5, opacity: diff < 0 ? 0.6 : 1, transition: "0.15s",
          "&:hover": { borderColor: "primary.main" },
        }}
      >
        <Box sx={{ width: 46, flex: "none", textAlign: "center", py: 0.4, borderRadius: 1, bgcolor: ddBg, color: ddColor, fontSize: "0.72rem", fontWeight: 700 }}>{ddLabel}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {new Date(e.date).getMonth() + 1}/{new Date(e.date).getDate()}({dow}) {e.start_time ? e.start_time.slice(0, 5) : "-"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {e.user?.name} · {e.teacher?.name ? `${e.teacher.name} 선생님` : "지정 없음"}
          </Typography>
        </Box>
        {count > 1 && (
          <Chip label={`${count}명`} size="small" title={`같은 시간대 ${count}명 대기`} sx={{ flex: "none", height: 20, fontSize: "0.68rem", fontWeight: 700, bgcolor: darkMode ? "rgba(255,152,0,0.2)" : "#fff3e0", color: "#e65100", "& .MuiChip-label": { px: 0.7 } }} />
        )}
      </Box>
    );
  };

  // 빈자리 대기 우측 상세: 대기 슬롯을 그 주 레슨 표에 시각화
  const renderWaitlistDetail = (entry) => {
    const diff = ddayOf(entry.date);
    const dow = dayNamesKo[new Date(entry.date).getDay()];
    const applied = entry.createdAt || entry.created_at;
    const teacherId = entry.teacher_id ?? entry.teacher?.id ?? null;
    const reqTime = entry.start_time ? entry.start_time.slice(0, 5) : "";
    let ddLabel;
    if (diff < 0) ddLabel = "지남";
    else if (diff === 0) ddLabel = "오늘";
    else if (diff === 1) ddLabel = "내일";
    else ddLabel = `D-${diff}`;
    const targetLesson = teacherId != null ? wlPanel.lessons.find((l) => l.date === entry.date && l.teacher_id === teacherId && l.start_time.slice(0, 5) === reqTime) : null;
    const targetBlocked = teacherId != null && (
      (!!targetLesson && (targetLesson.user?.role === "ADMIN" || targetLesson.user?.role === "TEACHER")) ||
      (!targetLesson && isBlockedBySchedule(teacherId, entry.date, reqTime))
    );
    // 대기 시간대에 출근하는 선생님이 없는 경우 (지정 선생님 미출근 / 지정 없음이면 전원 미출근)
    // — 변동 스케줄(OFF/ADJUSTED)이 있으면 그것을 반영해 판정
    const worksAtReq = (t) => worksAt(t, entry.date, reqTime, wlPanel.overrides);
    const targetOffDuty = !targetLesson && (
      teacherId != null
        ? (() => { const t = wlPanel.teachers.find((x) => x.id === teacherId); return !!t && !worksAtReq(t); })()
        : (wlPanel.teachers.length > 0 && !wlPanel.teachers.some(worksAtReq))
    );
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", pb: 1, mb: 1.25, borderBottom: 1, borderColor: "divider" }}>
          <Chip label="빈자리 대기" size="small" sx={{ backgroundColor: "rgba(142,36,170,0.14)", color: "#8e24aa", fontWeight: 600 }} />
          <Typography variant="subtitle1" fontWeight="bold">
            {entry.user?.name} <span style={{ color: "#9e9e9e" }}>→</span> {entry.teacher?.name ? `${entry.teacher.name} 선생님` : "선생님 지정 없음"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {entry.date} ({dow}) {reqTime} · {ddLabel}{applied ? ` · ${timeAgo(applied)} 신청` : ""}
          </Typography>
        </Box>

        {wlLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={24} /></Box>
        ) : (
          <>
            {targetBlocked ? (
              <Alert severity="warning" icon={false} sx={{ mb: 1.25, py: 0.25, "& .MuiAlert-message": { py: 0.5 } }}>
                <Typography variant="caption">
                  ⚠️ 이 시간대는 <strong>관리자가 블록(예약 차단)</strong>해둔 시간입니다. 회원이 예약 불가 상태를 보고 대기를 신청했으니, 블록을 해제하지 않으면 자리가 나지 않습니다.
                </Typography>
              </Alert>
            ) : targetOffDuty ? (
              <Alert severity="warning" icon={false} sx={{ mb: 1.25, py: 0.25, "& .MuiAlert-message": { py: 0.5 } }}>
                <Typography variant="caption">
                  ⚠️ 이 시간대는 {teacherId != null ? <><strong>{entry.teacher?.name} 선생님의 미출근 시간</strong></> : <><strong>출근하는 선생님이 없는 시간</strong></>}입니다. 예약 자체가 생길 수 없어 취소로 인한 빈자리 알림이 발송되지 않으니, 출근 스케줄을 열거나 회원에게 별도로 안내해 주세요.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" icon={false} sx={{ mb: 1.25, py: 0.25, "& .MuiAlert-message": { py: 0.5 } }}>
                <Typography variant="caption">
                  {targetLesson ? (
                    <>보라색 칸은 현재 <strong>{targetLesson.user?.name}</strong>님이 예약 중인 시간입니다. 이 예약이 취소되면 <strong>{entry.user?.name}</strong>님에게 자동으로 빈자리 알림이 발송됩니다.{teacherId == null ? " (선생님 지정 없음 — 해당 시간 모든 선생님 대상)" : ""}</>
                  ) : (
                    <>보라색 칸이 <strong>{entry.user?.name}</strong>님이 대기 신청한 시간대입니다.{teacherId == null ? " (선생님 지정 없음 — 해당 시간 모든 선생님 대상)" : ""} 빈자리가 나면 알림이 발송됩니다.</>
                  )}
                </Typography>
              </Alert>
            )}
            {renderWeekGrid(
              { date: entry.date, time: reqTime, teacherId, kind: "wait", memberName: entry.user?.name },
              wlPanel.lessons, wlPanel.teachers, wlPanel.weekDates || [], 2, wlPanel.overrides || {}
            )}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.75 }}>
              {[
                { t: "예약 없음", ghost: true },
                { t: "예약됨", bg: darkMode ? "rgba(255,167,38,0.16)" : "#fff3e0" },
                { t: "블록", bg: darkMode ? "rgba(255,255,255,0.13)" : "#e8eaed" },
                { t: "대기 대상", bg: "#8e24aa" },
                { t: "휴일", bg: darkMode ? "#1a1a1a" : "#222" },
              ].map((lg) => (
                <Box key={lg.t} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: lg.ghost ? "transparent" : lg.bg, border: lg.ghost ? "1px solid" : "none", borderColor: "divider" }} />
                  <Typography variant="caption" color="text.secondary">{lg.t}</Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  };

  // PC 빈자리 대기: 좌우 2단 콘솔 (임박순 목록 + 주간 표 시각화)
  const renderWaitlistConsole = () => {
    if (waitlist.length === 0) return null;
    const sorted = sortWaitlist(waitlist);
    const selected = sorted.find((w) => w.id === wlSelectedId) || null;
    const slotCount = {};
    waitlist.forEach((e) => {
      const k = `${e.date}_${e.start_time}`;
      slotCount[k] = (slotCount[k] || 0) + 1;
    });
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "300px minmax(0, 1fr)", gap: 2, height: "100%", minHeight: 0 }}>
        <Box sx={{ overflowY: "auto", minHeight: 0, pr: 0.5 }}>
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">임박순 · {sorted.length}건</Typography>
            {sorted.map((e) => renderWaitlistRow(e, slotCount[`${e.date}_${e.start_time}`], e.id === wlSelectedId))}
          </Stack>
        </Box>
        <Box sx={{ overflowY: "auto", minHeight: 0 }}>
          {selected ? (
            renderWaitlistDetail(selected)
          ) : (
            <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, py: 8, textAlign: "center" }}>
              <Typography color="text.secondary">왼쪽에서 대기 항목을 선택하세요.</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // 좌측 레일 / 모바일 탭 섹션 카운트
  const pendingCount = pendingRequests.length;
  const processedCount = requests.filter((r) => r.status !== "PENDING").length;
  const sectionMeta = [
    { key: "pending", label: "승인 대기", icon: <PendingActions />, badge: pendingCount },
    { key: "waitlist", label: "빈자리 대기", icon: <NotificationsActive />, badge: waitlist.length },
    { key: "history", label: "처리 내역", icon: <History />, badge: 0 },
  ];

  // PC 좌측 아이콘 레일 (관리자 페이지 사이드바와 동일 언어)
  const renderRail = () => (
    <Box sx={{ width: 96, flexShrink: 0, borderRight: 1, borderColor: "divider", py: 1, display: "flex", flexDirection: "column", gap: 0.5, bgcolor: darkMode ? "grey.900" : "grey.50" }}>
      {sectionMeta.map((it) => {
        const active = section === it.key;
        return (
          <Box
            key={it.key}
            role="button"
            aria-current={active ? "page" : undefined}
            onClick={() => setSection(it.key)}
            sx={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5,
              py: 1.25, mx: 0.75, borderRadius: 2, cursor: "pointer", userSelect: "none",
              color: active ? "primary.main" : "text.secondary",
              bgcolor: active ? "action.selected" : "transparent",
              transition: "background-color .15s",
              "&:hover": { bgcolor: active ? "action.selected" : "action.hover" },
              "& svg": { fontSize: 24 },
            }}
          >
            {it.badge > 0 ? (
              <Badge color="error" badgeContent={it.badge} max={99}>{it.icon}</Badge>
            ) : it.icon}
            <Typography sx={{ fontSize: 11, fontWeight: active ? 700 : 500, lineHeight: 1.15, textAlign: "center" }}>
              {it.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  // PC 섹션 헤더 바 (제목 + 카운트)
  const renderSectionHeader = () => {
    const label = section === "pending" ? "승인 대기" : section === "waitlist" ? "빈자리 대기" : "처리 내역";
    const count = section === "pending" ? pendingCount : section === "waitlist" ? waitlist.length : processedCount;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2.5, py: 1, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Typography variant="h6" fontWeight="bold">{label}</Typography>
        <Box component="span" sx={{ px: 1, minWidth: 24, textAlign: "center", borderRadius: 5, bgcolor: "action.selected", color: "text.secondary", fontSize: "0.8rem", fontWeight: 700, lineHeight: "22px" }}>{count}</Box>
        {section === "pending" && <Typography variant="caption" color="text.secondary">오래된 순부터 처리</Typography>}
      </Box>
    );
  };

  // 섹션 본문
  const renderSectionBody = () => {
    if (section === "waitlist") {
      if (waitlistLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>;
      if (waitlist.length === 0) return <Box sx={{ textAlign: "center", py: 6 }}><Typography color="text.secondary">빈자리 대기 신청이 없습니다.</Typography></Box>;
      return isMobile ? <Box>{waitlist.map(renderWaitlistCard)}</Box> : renderWaitlistConsole();
    }
    if (section === "history") {
      if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>;
      return renderHistory();
    }
    if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>;
    if (isMobile) {
      return pendingRequests.length === 0
        ? <Box sx={{ textAlign: "center", py: 6 }}><Typography color="text.secondary">승인 대기 신청이 없습니다.</Typography></Box>
        : <Box>{pendingRequests.map(renderMobileCard)}</Box>;
    }
    return renderPendingConsole();
  };

  return (
    <Box sx={{ pt: { xs: 2, sm: 0 }, pb: { xs: 8, sm: 0 }, px: { xs: 1, sm: 0 }, maxWidth: { xs: 1200, sm: "none" }, mx: "auto", height: { sm: "calc(100vh - 112px)" }, display: { sm: "flex" }, flexDirection: { sm: "column" }, minHeight: 0 }}>

      {isMobile && (
        <Tabs
          value={section}
          onChange={(_, v) => setSection(v)}
          variant="fullWidth"
          sx={{
            flexShrink: 0,
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: darkMode ? "grey.900" : "#fafafa",
            "& .MuiTab-root": { fontWeight: 600, textTransform: "none", fontSize: "0.72rem", minWidth: 0, px: 0.5 },
            "& .Mui-selected": { color: "primary.main" },
          }}
        >
          <Tab value="pending" label={tabLabel("승인 대기", pendingCount, "#ffa726")} />
          <Tab value="waitlist" label={tabLabel("빈자리 대기", waitlist.length, "#9c27b0")} />
          <Tab value="history" label={tabLabel("처리 내역", processedCount)} />
        </Tabs>
      )}

      <Box sx={{ bgcolor: "background.paper", flex: { sm: 1 }, minHeight: 0, display: { sm: "flex" }, flexDirection: { sm: "row" }, overflow: { sm: "hidden" } }}>
        {!isMobile && renderRail()}

        <Box sx={{ flex: { sm: 1 }, minWidth: 0, minHeight: 0, display: { sm: "flex" }, flexDirection: { sm: "column" }, overflow: { sm: "hidden" } }}>
          {!isMobile && renderSectionHeader()}
          <Box sx={{ flex: { sm: 1 }, minHeight: 0, overflow: { sm: section === "history" ? "auto" : "hidden" }, px: { xs: 0, sm: section === "history" ? 2.5 : 2 }, py: { xs: 1, sm: 1.5 } }}>
            {renderSectionBody()}
          </Box>
        </Box>
      </Box>

      {/* 승인 확인 다이얼로그 */}
      <Dialog
        open={approveDialog.open}
        onClose={() => setApproveDialog({ open: false, request: null, createPractice: true })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {approveDialog.request && typeConfig[approveDialog.request.type]?.label} 승인
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {approveDialog.request && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {approveDialog.request.user?.name}님의 {approveDialog.request.date}{" "}
                {approveDialog.request.start_time.slice(0, 5)} {typeConfig[approveDialog.request.type]?.label}을(를) 승인합니다.
              </Alert>
              {approveDialog.holidayWarn && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  이 날짜는 <b>휴일</b>로 지정되어 있습니다. 휴일 레슨으로 승인하려면 아래 버튼으로 한 번 더 확인해주세요.
                </Alert>
              )}
              {approveDialog.request.type === 'RESERVATION' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={approveDialog.createPractice}
                      onChange={(e) => setApproveDialog(d => ({ ...d, createPractice: e.target.checked }))}
                    />
                  }
                  label="연습실 예약 데이터 자동 생성"
                />
              )}
              {approveDialog.request.type === 'CANCELLATION' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={approveDialog.createPractice}
                      onChange={(e) => setApproveDialog(d => ({ ...d, createPractice: e.target.checked }))}
                    />
                  }
                  label="연습실 데이터 함께 제거"
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setApproveDialog({ open: false, request: null, createPractice: true })} disabled={actionLoading}>
            취소
          </Button>
          <Button
            onClick={() => handleConfirmApprove(approveDialog.holidayWarn === true)}
            variant="contained"
            color={approveDialog.holidayWarn ? "warning" : "success"}
            disabled={actionLoading}
          >
            {approveDialog.holidayWarn ? "휴일 레슨 승인" : "승인"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 반려 사유 다이얼로그 */}
      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, request: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {rejectDialog.request && typeConfig[rejectDialog.request.type]?.label} 반려
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {rejectDialog.request && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {rejectDialog.request.user?.name}님의 {rejectDialog.request.date}{" "}
                {rejectDialog.request.start_time.slice(0, 5)} {typeConfig[rejectDialog.request.type]?.label}을(를) 반려합니다.
              </Alert>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="반려 사유"
                placeholder="반려 사유를 입력해주세요"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialog({ open: false, request: null })} disabled={actionLoading}>
            취소
          </Button>
          <Button
            onClick={handleConfirmReject}
            variant="contained"
            color="error"
            disabled={actionLoading || !rejectionReason.trim()}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : "반려"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스케줄 확인 다이얼로그 */}
      <Dialog
        open={scheduleDialog.open}
        onClose={() => setScheduleDialog({ open: false, request: null, lessons: [], teachers: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          레슨 스케줄 확인
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {scheduleDialog.request && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  {scheduleDialog.request.type === 'RESERVATION' ? '레슨 신청' : '레슨 취소'} 요청 정보
                </Typography>
                <Typography variant="body2">
                  • 신청자: {scheduleDialog.request.user?.name}<br/>
                  • 선생님: {scheduleDialog.request.teacher?.name}<br/>
                  • 날짜: {scheduleDialog.request.date}<br/>
                  • 시간: {scheduleDialog.request.start_time.slice(0, 5)} ~ {scheduleDialog.request.end_time.slice(0, 5)}
                </Typography>
              </Alert>

              {isHolidayDate(scheduleDialog.request.date) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="caption">
                    이 날짜는 <strong>휴일</strong>로 지정되어 있습니다.
                    {scheduleDialog.request.type === "RESERVATION" && " 승인하려면 휴일 확인 단계를 거칩니다."}
                  </Typography>
                </Alert>
              )}

              {scheduleLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    {scheduleDialog.request.date} 레슨 스케줄 (±1시간)
                  </Typography>

                  {renderScheduleGrid(scheduleDialog.request, scheduleDialog.lessons, scheduleDialog.teachers)}

                  {scheduleDialog.request.type === 'RESERVATION' && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      <Typography variant="caption">
                        <strong>승인 시:</strong> 초록색으로 표시된 레슨이 추가됩니다.
                      </Typography>
                    </Alert>
                  )}

                  {scheduleDialog.request.type === 'CANCELLATION' && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="caption">
                        <strong>승인 시:</strong> 빨간색으로 표시된 레슨이 삭제되고, {scheduleDialog.request.user?.name}님의 티켓이 1개 복구됩니다.
                      </Typography>
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setScheduleDialog({ open: false, request: null, lessons: [], teachers: [] })}
          >
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LessonRequestManagementPage;